import { 
  RichTextContent, 
  RichTextRun, 
  GoogleSheetsRichText, 
  GoogleSheetsTextRun,
  GoogleSheetsTextFormat 
} from '../types/good-things';

/**
 * Converts our app's rich text format to Google Sheets rich text format
 */
export const convertToGoogleSheetsFormat = (richText: RichTextContent): GoogleSheetsRichText => {
  if (!richText.runs || richText.runs.length === 0) {
    return {};
  }

  const textFormatRuns: GoogleSheetsTextRun[] = richText.runs.map(run => {
    const googleRun: GoogleSheetsTextRun = {
      content: run.text
    };

    if (run.format) {
      const format: GoogleSheetsTextFormat = {};
      
      if (run.format.bold) format.bold = true;
      if (run.format.italic) format.italic = true;
      if (run.format.underline) format.underline = true;
      if (run.format.link) {
        format.link = { uri: run.format.link.url };
      }

      if (Object.keys(format).length > 0) {
        googleRun.format = format;
      }
    }

    return googleRun;
  });

  return {
    textFormatRuns
  };
};

/**
 * Converts Google Sheets rich text format to our app's format
 */
export const convertFromGoogleSheetsFormat = (googleRichText: GoogleSheetsRichText): RichTextContent => {
  if (!googleRichText.textFormatRuns || googleRichText.textFormatRuns.length === 0) {
    return {
      runs: [{ text: '' }],
      plainText: ''
    };
  }

  const runs: RichTextRun[] = googleRichText.textFormatRuns.map(googleRun => {
    const run: RichTextRun = {
      text: googleRun.content
    };

    if (googleRun.format) {
      const format: any = {};
      
      if (googleRun.format.bold) format.bold = true;
      if (googleRun.format.italic) format.italic = true;
      if (googleRun.format.underline) format.underline = true;
      if (googleRun.format.link) {
        format.link = { url: googleRun.format.link.uri };
      }

      if (Object.keys(format).length > 0) {
        run.format = format;
      }
    }

    return run;
  });

  const plainText = runs.map(run => run.text).join('');

  return {
    runs,
    plainText
  };
};

/**
 * Converts plain text to rich text format (for backward compatibility)
 */
export const convertPlainTextToRichText = (plainText: string): RichTextContent => {
  return {
    runs: [{ text: plainText }],
    plainText
  };
};

/**
 * Extracts plain text from rich text content
 */
export const extractPlainText = (richText: RichTextContent): string => {
  if (richText.plainText) {
    return richText.plainText;
  }
  
  return richText.runs.map(run => run.text).join('');
};

/**
 * Checks if rich text content has any formatting
 */
export const hasFormatting = (richText: RichTextContent): boolean => {
  return richText.runs.some(run => run.format && Object.keys(run.format).length > 0);
};

/**
 * Sanitizes rich text content to prevent XSS attacks
 */
export const sanitizeRichText = (richText: RichTextContent): RichTextContent => {
  const sanitizedRuns = richText.runs.map(run => {
    const sanitizedRun: RichTextRun = {
      text: run.text.replace(/<[^>]*>/g, '') // Remove HTML tags
    };

    if (run.format) {
      const sanitizedFormat: any = {};
      
      // Only allow safe formatting options
      if (run.format.bold) sanitizedFormat.bold = true;
      if (run.format.italic) sanitizedFormat.italic = true;
      if (run.format.underline) sanitizedFormat.underline = true;
      
      // Sanitize links
      if (run.format.link) {
        const url = run.format.link.url;
        // Basic URL validation - only allow http/https
        if (url.startsWith('http://') || url.startsWith('https://')) {
          sanitizedFormat.link = { url };
        }
      }

      if (Object.keys(sanitizedFormat).length > 0) {
        sanitizedRun.format = sanitizedFormat;
      }
    }

    return sanitizedRun;
  });

  const plainText = sanitizedRuns.map(run => run.text).join('');

  return {
    runs: sanitizedRuns,
    plainText
  };
};

/**
 * Merges consecutive runs with the same formatting to optimize storage
 */
export const optimizeRichText = (richText: RichTextContent): RichTextContent => {
  if (richText.runs.length <= 1) {
    return richText;
  }

  const optimizedRuns: RichTextRun[] = [];
  let currentRun = richText.runs[0];

  for (let i = 1; i < richText.runs.length; i++) {
    const nextRun = richText.runs[i];
    
    // Check if formatting is the same
    const currentFormat = JSON.stringify(currentRun.format || {});
    const nextFormat = JSON.stringify(nextRun.format || {});
    
    if (currentFormat === nextFormat) {
      // Merge the text
      currentRun = {
        text: currentRun.text + nextRun.text,
        format: currentRun.format
      };
    } else {
      // Different formatting, save current and start new
      optimizedRuns.push(currentRun);
      currentRun = nextRun;
    }
  }
  
  // Don't forget the last run
  optimizedRuns.push(currentRun);

  const plainText = optimizedRuns.map(run => run.text).join('');

  return {
    runs: optimizedRuns,
    plainText
  };
};

/**
 * Creates a rich text content from HTML string (for contentEditable parsing)
 */
export const parseHTMLToRichText = (html: string): RichTextContent => {
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const runs: RichTextRun[] = [];
  
  const parseNode = (node: Node, inheritedFormat: any = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (text) {
        runs.push({
          text,
          format: Object.keys(inheritedFormat).length > 0 ? { ...inheritedFormat } : undefined
        });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const newFormat = { ...inheritedFormat };
      
      // Parse formatting from element
      if (element.tagName === 'B' || element.tagName === 'STRONG' || 
          element.style.fontWeight === 'bold') {
        newFormat.bold = true;
      }
      if (element.tagName === 'I' || element.tagName === 'EM' || 
          element.style.fontStyle === 'italic') {
        newFormat.italic = true;
      }
      if (element.tagName === 'U' || element.style.textDecoration === 'underline') {
        newFormat.underline = true;
      }
      if (element.tagName === 'A') {
        const href = element.getAttribute('href');
        if (href) {
          newFormat.link = { url: href };
        }
      }
      
      // Recursively parse children
      for (const child of Array.from(node.childNodes)) {
        parseNode(child, newFormat);
      }
    }
  };

  parseNode(tempDiv);
  
  const plainText = runs.map(run => run.text).join('');
  
  return optimizeRichText({
    runs: runs.length > 0 ? runs : [{ text: plainText }],
    plainText
  });
};

/**
 * Converts rich text to HTML for display
 */
export const convertRichTextToHTML = (richText: RichTextContent): string => {
  if (!richText.runs || richText.runs.length === 0) {
    return '';
  }

  return richText.runs.map(run => {
    let html = run.text;
    
    // Convert line breaks to <br> tags
    html = html.replace(/\n/g, '<br>');
    
    if (run.format) {
      if (run.format.link) {
        html = `<a href="${run.format.link.url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">${html}</a>`;
      }
      if (run.format.underline) {
        html = `<u>${html}</u>`;
      }
      if (run.format.italic) {
        html = `<i>${html}</i>`;
      }
      if (run.format.bold) {
        html = `<b>${html}</b>`;
      }
    }
    
    return html;
  }).join('');
};